import React, { useContext, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import IconInCircle from '../Icons/IconInCircle'

import * as Analytics from 'analytics/analytics'
import {
  getListName,
  productClick,
  productImpression,
} from 'analytics/enhanced-ecommerce'
import {
  GAImpressionFieldObject,
  tripReengagementCardEvent,
} from 'analytics/eventDefinitions'
import { clientEvent } from 'analytics/snowplow/events'
import { fireInteractionEvent } from 'api/googleTagManager'
import Image from 'components/Common/Image'
import Spacing from 'components/Common/Spacing/Spacing'
import StickyPromptCard from 'components/Common/StickyPromptCard/StickyPromptCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import SolidCheckIcon from 'components/Luxkit/Icons/solid/SolidCheckIcon'
import SolidExclamationTriangleIcon from 'components/Luxkit/Icons/solid/SolidExclamationTriangleIcon'
import SolidHeartIcon from 'components/Luxkit/Icons/solid/SolidHeartIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import { countWhere } from 'lib/arrayUtils'
import noop from 'lib/noop'
import uuidV4 from 'lib/uuidV4'
import { setTripReengagementDismissedInStorage } from 'storage/tripReengagement'
import { useTripLowStockItems, useTrip } from 'tripPlanner/hooks/api'
import { BasicTrip } from 'tripPlanner/types/common'
import { formatDateRange, itemIsBooked } from 'tripPlanner/utils'

const EVENT_CATEGORY = 'trip-reengagement-card'

const MainContent = styled(Group)`
  flex: 1;
`

interface Props {
  trip: BasicTrip
  onClose?: () => void
  show: boolean
  stickyTop?: number
}

function TripReengagementCard({
  trip: basicTrip,
  onClose = noop,
  show,
  stickyTop,
}: Props) {
  const { data: fullTrip } = useTrip({
    tripId: basicTrip.id,
    enabled: show,
  })

  const { data: lowStockTripItem } = useTripLowStockItems({
    tripId: basicTrip.id,
  })

  const [inViewRef, inView] = useInView({ triggerOnce: true })

  const listId = useRef<string>()
  const listName = getListName(
    'TripPlanner',
    'global',
    'Trip Re-engagement Card',
  )
  const { currentCurrency } = useContext(GeoContext)

  useEffect(() => {
    if (show && inView) {
      listId.current = uuidV4()
      const impression: GAImpressionFieldObject = {
        id: basicTrip.id,
        name: basicTrip.name,
        list: listName,
        category: EVENT_CATEGORY,
        quantity: 1,
      }
      productImpression(currentCurrency, [impression], {
        listId: listId.current,
        listName,
      })
      fireInteractionEvent(tripReengagementCardEvent('view'))
    }
  }, [basicTrip.id, basicTrip.name, currentCurrency, listName, show, inView])

  const onClick = () => {
    if (listId.current) {
      productClick(
        currentCurrency,
        {
          id: basicTrip.id,
          name: basicTrip.name,
          category: EVENT_CATEGORY,
          quantity: 1,
        },
        {
          listId: listId.current,
          listName,
        },
      )
      fireInteractionEvent(tripReengagementCardEvent('click'))
    }
    setTripReengagementDismissedInStorage('clicked')
    onClose()
  }

  const onDismiss = () => {
    if (listId.current) {
      Analytics.track(
        'client-event',
        clientEvent(EVENT_CATEGORY, 'dismissed', 'trip_planner', 'interaction'),
      )
      fireInteractionEvent(tripReengagementCardEvent('dismiss'))
    }
    setTripReengagementDismissedInStorage('closed')
    onClose()
  }

  let bookedSavedContent: React.ReactNode = null

  if (fullTrip) {
    const bookedCount = countWhere(fullTrip.items, itemIsBooked)
    const savedCount = countWhere(
      fullTrip.items,
      (item) => item.sourceType !== 'NA' && !itemIsBooked(item),
    )

    bookedSavedContent = (
      <>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <IconInCircle
            icon={SolidCheckIcon}
            size={16}
            iconSize={13}
            circleColour={bookedCount > 0 ? 'success' : 'neutral-four'}
          />
          <BodyText variant="medium">{bookedCount} booked</BodyText>
        </Group>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <IconInCircle
            icon={SolidHeartIcon}
            size={16}
            iconSize={10}
            circleColour={savedCount > 0 ? 'favourite' : 'neutral-four'}
          />
          <BodyText variant="medium">{savedCount} saved</BodyText>
        </Group>
      </>
    )
  }

  return (
    <StickyPromptCard
      onClose={onDismiss}
      to={`/trip-planner/trip/${basicTrip.id}/summary`}
      onClick={onClick}
      show={show}
      stickyTop={stickyTop}
      ref={inViewRef}
    >
      <StickyPromptCard.Header>
        <Heading variant="heading6">Keep planning your trip</Heading>
      </StickyPromptCard.Header>
      <StickyPromptCard.Content>
        <Group direction="horizontal" gap={16}>
          <MainContent direction="vertical" gap={8}>
            <BodyText variant="large" lineClamp={2}>
              {basicTrip.name}
            </BodyText>
            {basicTrip.startDate && basicTrip.endDate && (
              <Caption variant="large">
                {formatDateRange(basicTrip.startDate, basicTrip.endDate)}
              </Caption>
            )}
            {lowStockTripItem && (
              <Group direction="horizontal" gap={16}>
                <AssistChip
                  variant="filled"
                  size="small"
                  to={`/trip-planner/trip/${lowStockTripItem.tripId}/summary?#tripitem-${lowStockTripItem?.id}`}
                  endIcon={<SolidExclamationTriangleIcon colour="warning" />}
                  onClick={onClick}
                >
                  Limited availability
                </AssistChip>
              </Group>
            )}
          </MainContent>
          <Image
            id={basicTrip.imageId}
            aspectRatio="1:1"
            width={68}
            height={68}
          />
        </Group>
      </StickyPromptCard.Content>
      <StickyPromptCard.Footer>
        <Spacing pt={16}>
          <Group
            direction="horizontal"
            gap={12}
            tabletGap={16}
            horizontalAlign="space-between"
            verticalAlign="end"
          >
            <Group
              direction="horizontal"
              gap="8 16"
              horizontalAlign="space-between"
              verticalAlign="center"
              wrap="wrap"
            >
              {bookedSavedContent}
            </Group>
            <TextButton
              variant="default"
              kind="primary"
              size="small"
              nonInteractable
            >
              Continue planning
            </TextButton>
          </Group>
        </Spacing>
      </StickyPromptCard.Footer>
    </StickyPromptCard>
  )
}

export default TripReengagementCard
